import { createContext, useContext, useEffect, useState } from 'react';
import { api } from '../api';

// Shared fund + as-of date selection, used by the top filter bar on every page.
const FundContext = createContext(null);

export function FundProvider({ children }) {
  const [funds, setFunds] = useState([]);
  // No hardcoded defaults — the fund comes from the API's fund list and the date
  // is resolved to each fund's latest available report date (see effects below).
  const [fund, setFund] = useState('');
  const [date, setDate] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Load the fund list once and select the first fund by default.
  useEffect(() => {
    api
      .funds()
      .then((d) => {
        const list = d.funds || [];
        setFunds(list);
        setFund((prev) => {
          if (list.some((f) => f.name === prev)) return prev; // keep valid selection
          // Prefer the umbrella fund (no "_Class" share-class suffix). It carries
          // fund-level performance and always has data — the same fund Seamless
          // opens on. The raw API list can start with a data-less share class, so
          // don't just take list[0]. Fall back to the first fund otherwise.
          const umbrella = list.find((f) => !/_Class\b/i.test(f.name));
          return umbrella?.name || list[0]?.name || prev;
        });
      })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  // Whenever the selected fund changes, resolve its latest available report date
  // from the API so the dashboard opens on real data rather than a fixed day.
  useEffect(() => {
    if (!fund) return undefined;
    let active = true;
    api
      .latestDate(fund)
      .then((d) => {
        if (!active) return;
        if (d.date) setDate(d.date);
        // Fund has no reports at all — fall back to today so the picker isn't blank.
        else setDate((prev) => prev || new Date().toISOString().slice(0, 10));
      })
      .catch(() => {
        if (active) setDate((prev) => prev || new Date().toISOString().slice(0, 10));
      });
    return () => { active = false; };
  }, [fund]);

  return (
    <FundContext.Provider value={{ funds, fund, setFund, date, setDate, loading, error }}>
      {children}
    </FundContext.Provider>
  );
}

export const useFund = () => useContext(FundContext);
